/**
 * A finding's history as readable events: who did what, in one short
 * sentence, with the detail worth reading under it. Read from the document's
 * activity trail (`PolicyActivity`), filtered to this finding, and merged into
 * the finding's one timeline beside its comments and Inspector's replies.
 *
 * The trail stores a raw kind and a free-text detail that usually repeats the
 * finding's title; this module turns both into sentences, names the actor
 * from the organization's people, and drops the parts the page already says.
 * Every kind the database can write has a sentence; an unknown one still reads
 * as "updated it", never as its raw identifier.
 *
 * @module components/regulation/finding/findingActivity
 */
import { DISMISS_CATEGORY_LABELS } from "@repo/shared/findingPlanning";

import type { OrganizationPerson, PolicyActivityEntry } from "../../../state/governance";
import { readableEmail } from "../reviewConversationView";
import type { FindingItem } from "./findingItem";

export type FindingActivityTone =
  | "raised"
  | "resolved"
  | "dismissed"
  | "reopened"
  | "planned"
  | "revised"
  | "withdrawn"
  | "undone"
  | "proposed"
  | "progress"
  | "checked"
  | "unchecked"
  | "document"
  | "other";

export interface FindingActivityEvent {
  readonly id: string;
  readonly createdAt: string;
  /** A person's name, "Inspector", or a readable stand-in. */
  readonly actor: string;
  /** True when Inspector (an agent or an automated sweep) did it. */
  readonly inspector: boolean;
  /** Lower-case sentence that follows the actor: "resolved it". */
  readonly text: string;
  readonly detail: string | null;
  readonly tone: FindingActivityTone;
}

type Sentence = { readonly text: string; readonly tone: FindingActivityTone; readonly detail: "none" | "suffix" | "full" };

/**
 * Every kind `PolicyActivityKind` names. `detail`: "none" when the stored
 * detail is only the subject's title, "suffix" when it is
 * `<title> — <what matters>`, "full" when the whole detail is worth reading.
 */
const SENTENCES: Readonly<Record<string, Sentence>> = {
  finding_attached: { text: "raised it", tone: "raised", detail: "none" },
  recommendation_raised: { text: "raised it", tone: "raised", detail: "none" },
  finding_accepted: { text: "marked it in progress", tone: "progress", detail: "none" },
  recommendation_accepted: { text: "marked it in progress", tone: "progress", detail: "none" },
  finding_resolved: { text: "resolved it", tone: "resolved", detail: "suffix" },
  recommendation_applied: { text: "resolved it", tone: "resolved", detail: "none" },
  finding_closed: { text: "dismissed it", tone: "dismissed", detail: "none" },
  finding_dismissed: { text: "dismissed it", tone: "dismissed", detail: "none" },
  recommendation_dismissed: { text: "dismissed it", tone: "dismissed", detail: "none" },
  finding_reopened: { text: "reopened it", tone: "reopened", detail: "none" },
  recommendation_reopened: { text: "reopened it", tone: "reopened", detail: "none" },
  finding_revised: { text: "revised the assessment", tone: "revised", detail: "none" },
  recommendation_revised: { text: "revised the suggestion", tone: "revised", detail: "none" },
  finding_withdrawn: { text: "withdrew it", tone: "withdrawn", detail: "suffix" },
  recommendation_withdrawn: { text: "withdrew it", tone: "withdrawn", detail: "suffix" },
  finding_planned: { text: "changed the assignee or due date", tone: "planned", detail: "none" },
  recommendation_planned: { text: "changed the assignee or due date", tone: "planned", detail: "none" },
  recommendation_undone: { text: "undid the resolution, so it is open again", tone: "undone", detail: "none" },
  fix_check_confirmed: { text: "ticked a checklist item", tone: "checked", detail: "full" },
  fix_check_unconfirmed: { text: "unticked a checklist item", tone: "unchecked", detail: "full" },
  fix_fill_set: { text: "filled in a checklist value", tone: "checked", detail: "full" },
  mapping_changed: { text: "changed the control mapping", tone: "revised", detail: "full" },
  kind_changed: { text: "changed the document type", tone: "document", detail: "full" },
  created: { text: "created the document", tone: "document", detail: "none" },
  published: { text: "published the document", tone: "document", detail: "full" },
  archived: { text: "archived the document", tone: "document", detail: "none" },
  reviewed: { text: "reviewed the document", tone: "document", detail: "full" },
  owner_changed: { text: "changed the document owner", tone: "document", detail: "full" },
  owners_changed: { text: "changed the document owners", tone: "document", detail: "full" },
  review_changed: { text: "changed the review schedule", tone: "document", detail: "full" },
  file_replaced: { text: "uploaded a new file", tone: "document", detail: "full" },
};

const RAISED = new Set(["finding_attached", "recommendation_raised"]);
const RESOLVED = new Set(["finding_resolved", "recommendation_applied"]);
const DISMISSED = new Set(["finding_closed", "finding_dismissed", "recommendation_dismissed"]);
/** The prefix the database writes on a proposal's receipt (`finding-changes.ts`). */
const PROPOSAL_PREFIX = "Proposed PDF changes:";
const SEPARATOR = " — ";
const CLEARED_PREFIX = "Cleared ";

const personName = (person: OrganizationPerson) => person.name?.trim() || readableEmail(person.email);

/**
 * Who acted. The API records an agent's or a sweep's write with no actor
 * (`actorOf` in `apps/api/src/handlers.ts`), so a missing actor is Inspector.
 */
export function activityActor(
  entry: Pick<PolicyActivityEntry, "actorId" | "actorEmail">,
  peopleById: ReadonlyMap<string, OrganizationPerson>,
): { readonly name: string; readonly inspector: boolean } {
  if (entry.actorId === null) return { name: "Inspector", inspector: true };
  const person = peopleById.get(entry.actorId);
  if (person) return { name: personName(person), inspector: false };
  return { name: entry.actorEmail ? readableEmail(entry.actorEmail) : "Someone", inspector: false };
}

/** The part of a stored detail the page does not already say. */
function readDetail(detail: string | null, title: string, mode: Sentence["detail"]): string | null {
  const text = detail?.trim();
  if (!text || mode === "none" || text === title.trim()) return null;
  // A check's receipt leads with its verb ("Confirmed: …"); the sentence says it already.
  if (mode === "full") return text.replace(/^(?:Confirmed|Unconfirmed):\s*/, "") || null;
  const rest = text.startsWith(title.trim() + SEPARATOR)
    ? text.slice(title.trim().length + SEPARATOR.length)
    : text.includes(SEPARATOR)
      ? text.slice(text.indexOf(SEPARATOR) + SEPARATOR.length)
      : null;
  return rest?.trim() || null;
}

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** One trail entry as an event, or null for what the conversation already shows. */
export function findingActivityEvent(
  entry: PolicyActivityEntry,
  context: { readonly title: string; readonly peopleById: ReadonlyMap<string, OrganizationPerson>; readonly raisedBefore?: boolean },
): FindingActivityEvent | null {
  const actor = activityActor(entry, context.peopleById);
  const base = { id: entry.id, createdAt: entry.createdAt, actor: actor.name, inspector: actor.inspector };
  if (entry.kind === "comment") {
    // A comment's receipt duplicates the comment itself, which the timeline
    // shows in full; a proposal's receipt is the only record of the proposal.
    const detail = entry.detail?.trim() ?? "";
    if (!detail.startsWith(PROPOSAL_PREFIX)) return null;
    return { ...base, text: "proposed changes to the document", tone: "proposed", detail: detail.slice(PROPOSAL_PREFIX.length).trim() || null };
  }
  // `fix_fill_set` records a clear too ("Cleared <label>", finding-fix-review.ts).
  const cleared = entry.kind === "fix_fill_set" && entry.detail?.trim().startsWith(CLEARED_PREFIX);
  if (cleared) {
    const label = entry.detail!.trim().slice(CLEARED_PREFIX.length).trim();
    return { ...base, text: "cleared a checklist value", tone: "unchecked", detail: label || null };
  }
  const sentence = SENTENCES[entry.kind];
  if (!sentence) return { ...base, text: "updated it", tone: "other", detail: entry.detail?.trim() || null };
  if (RAISED.has(entry.kind) && context.raisedBefore) {
    // A second attach re-reads the same finding rather than raising a new one.
    return { ...base, text: "updated the assessment", tone: "revised", detail: null };
  }
  let detail = readDetail(entry.detail, context.title, sentence.detail);
  let text = sentence.text;
  const version = entry.kind === "finding_resolved" && detail ? /^resolved by v(\d+)$/i.exec(detail) : null;
  if (version) {
    text = `resolved it with File v${version[1]}`;
    detail = null;
  }
  return { ...base, text, tone: sentence.tone, detail: detail ? capitalize(detail) : null };
}

const SYNTHETIC_RAISER: Readonly<Record<FindingItem["raisedBy"], { actor: string; inspector: boolean; text: string }>> = {
  Inspector: { actor: "Inspector", inspector: true, text: "raised it" },
  Monitor: { actor: "Monitor", inspector: true, text: "raised it" },
  eve: { actor: "eve", inspector: true, text: "raised it" },
  "Added by hand": { actor: "Someone", inspector: false, text: "added it by hand" },
};

/** Within this window a stored decision and a trail entry are the same event. */
const SAME_DECISION_MS = 5 * 60_000;

/**
 * The finding's events, oldest first. The trail is per document and capped,
 * and a missing document has no document to file it under, so the raise and
 * the decision of record are filled in from the item itself when the trail
 * lacks them.
 */
export function findingActivityEvents(
  entries: ReadonlyArray<PolicyActivityEntry>,
  item: Pick<FindingItem, "id" | "title" | "raisedBy" | "raisedAt" | "decision">,
  peopleById: ReadonlyMap<string, OrganizationPerson>,
): FindingActivityEvent[] {
  const own = entries
    .filter((entry) => entry.subjectId === item.id)
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt) || a.id.localeCompare(b.id));
  const events: FindingActivityEvent[] = [];
  let raised = false;
  for (const entry of own) {
    const event = findingActivityEvent(entry, { title: item.title, peopleById, raisedBefore: raised });
    if (RAISED.has(entry.kind)) raised = true;
    if (event) events.push(event);
  }
  if (!raised && item.raisedAt > 0) {
    const raiser = SYNTHETIC_RAISER[item.raisedBy];
    events.unshift({
      id: `raised:${item.id}`,
      createdAt: new Date(item.raisedAt).toISOString(),
      actor: raiser.actor,
      inspector: raiser.inspector,
      text: raiser.text,
      detail: null,
      tone: "raised",
    });
  }
  const decision = item.decision;
  const decidedAt = decision?.at ? Date.parse(decision.at) : Number.NaN;
  if (decision && !Number.isNaN(decidedAt)) {
    const category = decision.status === "dismissed" && decision.category ? DISMISS_CATEGORY_LABELS[decision.category] : null;
    const detail = [category, decision.evidence, decision.note].filter(Boolean).join(" · ");
    // The trail's receipt names only the finding, so the decision of record
    // lends it the reason, the evidence and the note.
    const receipts = decision.status === "resolved" ? RESOLVED : DISMISSED;
    const receipt = own
      .filter((entry) => receipts.has(entry.kind) && Math.abs(Date.parse(entry.createdAt) - decidedAt) <= SAME_DECISION_MS)
      .sort((a, b) => Math.abs(Date.parse(a.createdAt) - decidedAt) - Math.abs(Date.parse(b.createdAt) - decidedAt))[0];
    if (receipt) {
      const index = events.findIndex((event) => event.id === receipt.id);
      const event = events[index];
      if (event && !event.detail && detail) events[index] = { ...event, detail };
    } else {
      const by = decision.by;
      const person = by ? [...peopleById.values()].find((candidate) => candidate.email.toLowerCase() === by.toLowerCase()) : undefined;
      events.push({
        id: `decision:${item.id}`,
        createdAt: new Date(decidedAt).toISOString(),
        actor: person ? personName(person) : by ? readableEmail(by) : "Someone",
        inspector: false,
        text: decision.status === "resolved" ? "resolved it" : "dismissed it",
        detail: detail || null,
        tone: decision.status,
      });
    }
  }
  return events;
}
